import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { applyCsrfHeader, withCsrfFailureMessage } from "../utils/csrf";
import { id } from "date-fns/locale";
// Helper function to convert to 12-hour format
const convertTo12HourFormat = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const convertedHours = hours % 12 || 12; // Convert 0 or 12 to 12 for AM/PM format
  return `${convertedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  credentials: "include", // Send HttpOnly pms_session cookie for silent SSO verification
  prepareHeaders: (headers) => {
    // Rely on HttpOnly cookie; mutating requests also need the CSRF header
    return applyCsrfHeader(headers);
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  // Sync auto-refreshed token from response headers into localStorage
  if (result.meta?.response?.headers) {
    const authHeader = result.meta.response.headers.get("authorization");
    if (authHeader && authHeader.startsWith("Bearer ")) {
      // Do nothing, rely on HttpOnly cookie
    }
  }

  // If 401 occurs, silently refresh token using pms_session cookie and retry query
  if (result.error && result.error.status === 401) {
    try {
      const refreshResult = await rawBaseQuery("sso/session-user", api, extraOptions);
      if (refreshResult.data && refreshResult.data.status && (refreshResult.data.token || refreshResult.data.access_token)) {
        const freshToken = refreshResult.data.token || refreshResult.data.access_token;
        localStorage.setItem("access_token", "secure-cookie-auth"); // Dummy token
        result = await rawBaseQuery(args, api, extraOptions);
      }
    } catch (e) { }
  }
  return result;
};

// Define the API slice
const apiSlice = createApi({
  reducerPath: "logDatas", // Matches the key in your store
  tagTypes: [
    "Timesheet",
    "Milestone",
    "AdditionalScope",
    "ProjectDetail",
    "BudgetOverview",
    "InvoicesQueue",
    "InvoicesSources",
    "InvoiceProjects",
    "InvoiceDetail",
    "ResourceEarnings",
    "AwaitingAllocation",
  ],
  baseQuery: withCsrfFailureMessage(
    fetchBaseQuery({
      baseUrl: process.env.REACT_APP_API_BASE_URL, // Replace with your base URL
      credentials: "include", // MUST include this to send the pms_session cookie
      prepareHeaders: (headers) => {
        // Intentionally not setting Authorization header
        // The backend validates the pms_session cookie; mutating requests must
        // also echo the XSRF-TOKEN cookie in the X-XSRF-TOKEN header.
        return applyCsrfHeader(headers);
      },
    })
  ),
  endpoints: (builder) => ({
    fetchLogDetails: builder.query({
      query: (id) => `task/timelog/show/${id}`, // Define endpoint with dynamic id
      transformResponse: (response) => {
        // Transform the data before returning it
        //console.log("responseee :", response);
        if (!response || !response.time_logs || !Array.isArray(response.time_logs)) {
          return [];
        }
        return response.time_logs.map((log, index) => ({
          id: index,
          Date: log.log_date,
          userName: log.owner_name,
          logHourse: [
            `${log.total_hours}`,
            `${log.billable_hours}`,
            `${log.non_billable_hours}`,
          ],
          timePeriod: "",
          dashBoard: "",
          isTask: true,
          subtasks: Array.isArray(log.time_logs)
            ? log.time_logs.map((timeLog, index) => {
              const text = timeLog.notes;
              const clearNotes = document.createElement("div");
              clearNotes.innerHTML = text;
              const textNotes = clearNotes.innerText.trim();
              const timePeriod =
                timeLog.start_time == null || timeLog.end_time == null
                  ? ""
                  : `${convertTo12HourFormat(
                    timeLog.start_time
                  )} - ${convertTo12HourFormat(timeLog.end_time)}`;
              return {
                id: timeLog.time_logs_id, // Include unique id
                timeLogId: timeLog.time_logs_id, // Include time_logs_id
                user: timeLog.log_title,
                logHourse: timeLog.hours_in_text,
                Date: timeLog.log_date,
                timePeriod: timePeriod,
                dashBoard: textNotes,
                start_time: timeLog.start_time, // Add start_time
                end_time: timeLog.end_time, // Add end_time
                billingType: timeLog.billing_type,
                added_name: timeLog.added_name,
                isSubtask: true,
                project_title: timeLog.project_title,
                timesheet_type: timeLog.timesheet_type,
                reported_manager_id: timeLog.reported_manager_id,
                reported_manager_name: timeLog.reported_manager_name,
                approval_status: timeLog.approval_status,
                parentId: timeLog.project_id,
              };
            })
            : [],
        }));
      },
      providesTags: ["Timesheet"],
    }),
    fetchTimeSheetLogDetails: builder.query({
      // query: ({ id, project_span, from_date, to_date }) =>
      //   `timesheet/show/${id}?project_span=${project_span}&from_date=${from_date}&to_date=${to_date}`,
      query: ({
        id,
        project_span,
        from_date,
        to_date,
        view_type,
        group_by,
        milestone_id,
      }) => {
        const params = {
          project_span,
          from_date,
          to_date,
          view_type: view_type ?? 6,
          groupBy: group_by,
        };
        if (milestone_id) {
          params.milestone_id = milestone_id;
        }
        return {
          url: `timesheet/show/${id}`,
          params,
        };
      },
      transformResponse: (response) => {
        if (!response || !response.time_logs || !Array.isArray(response.time_logs)) {
          return [];
        }
        return response.time_logs.map((log, index) => ({
          id: index,
          Date: log.log_date,
          userName: log.group_by,
          logHourse: [
            `${log.total_hours}`,
            `${log.billable_hours}`,
            `${log.non_billable_hours}`,
          ],
          timePeriod: "",
          dashBoard: "",
          isTask: true,
          isSubtask: false,
          subtasks: Array.isArray(log.time_logs)
            ? log.time_logs.map((timeLog) => {
              // const clearNotes = document.createElement("div");
              // clearNotes.innerHTML = timeLog.notes;
              // const textNotes = clearNotes.innerText.trim();
              const timePeriod =
                timeLog.start_time == null || timeLog.end_time == null
                  ? ""
                  : `${convertTo12HourFormat(
                    timeLog.start_time
                  )} - ${convertTo12HourFormat(timeLog.end_time)}`;

              return {
                id: timeLog.time_logs_id,
                timeLogId: timeLog.time_logs_id,
                user: timeLog.log_title,
                logHourse: timeLog.hours_in_text,
                Date: timeLog.log_date,
                timePeriod: timePeriod,
                dashBoard: timeLog.notes,
                other_log_entries: timeLog.other_log_entries,
                task_id: timeLog.task_id,
                issue_id: timeLog.issue_id,
                owner_id: timeLog.user_id || timeLog.owner_id,
                start_time: timeLog.start_time,
                end_time: timeLog.end_time,
                billingType: timeLog.billing_type,
                ownerName: timeLog.owner_name || timeLog.group_by,
                task_general_issue: timeLog.task_general_issue,
                added_name: timeLog.added_name,
                isSubtask: true,
                project_title: timeLog.project_title,
                timesheet_type: timeLog.timesheet_type,
                reported_manager_id: timeLog.reported_manager_id,
                reported_manager_name: timeLog.reported_manager_name,
                approval_status: timeLog.approval_status,
                milestone: timeLog.milestone,
                parentId: timeLog.project_id,
              };
            })
            : [],
        }));
      },
      providesTags: ["Timesheet", "Milestone"],
    }),

    fetchIssueTimeSheetLogDetails: builder.query({
      query: (id) => `issue/timelog/show/${id}`, // Define endpoint with dynamic id
      transformResponse: (response) => {
        //console.log("get flated slice  response :", response);
        // Transform the data before returning it
        return response.time_logs.map((log, index) => ({
          id: index,
          Date: log.log_date,
          userName: log.owner_name,
          logHourse: [
            `${log.total_hours}`,
            `${log.billable_hours}`,
            `${log.non_billable_hours}`,
          ],
          timePeriod: "",
          dashBoard: "",
          isTask: true,
          subtasks: Array.isArray(log.time_logs)
            ? log.time_logs.map((timeLog, index) => {
              const text = timeLog.notes;
              const clearNotes = document.createElement("div");
              clearNotes.innerHTML = text;
              const textNotes = clearNotes.innerText.trim();
              const timePeriod =
                timeLog.start_time == null || timeLog.end_time == null
                  ? ""
                  : `${convertTo12HourFormat(
                    timeLog.start_time
                  )} - ${convertTo12HourFormat(timeLog.end_time)}`;
              return {
                id: timeLog.time_logs_id, // Include unique id
                timeLogId: timeLog.time_logs_id, // Include time_logs_id
                user: timeLog.log_title,
                logHourse: timeLog.hours_in_text,
                Date: timeLog.log_date,
                timePeriod: timePeriod,
                dashBoard: textNotes,
                start_time: timeLog.start_time, // Add start_time
                end_time: timeLog.end_time, // Add end_time
                billingType: timeLog.billing_type,
                added_name: timeLog.added_name,
                isSubtask: true,
                project_title: timeLog.project_title,
                timesheet_type: timeLog.timesheet_type,
                reported_manager_id: timeLog.reported_manager_id,
                reported_manager_name: timeLog.reported_manager_name,
                approval_status: timeLog.approval_status,
                parentId: timeLog.project_id,
              };
            })
            : [],
        }));
      },
      providesTags: ["Timesheet"],
    }),
    fetchMailstoneTimeSheetLogDetails: builder.query({
      query: (id) => `milestones/issue/timelog/show/${id}`,
      transformResponse: (response) => {
        //console.log("get flaed slice  response :", response);
        return response.time_logs.map((log, index) => ({
          id: index,
          Date: log.log_date,
          userName: log.owner_name,
          logHourse: [
            `${log.total_hours}`,
            `${log.billable_hours}`,
            `${log.non_billable_hours}`,
          ],
          timePeriod: "",
          dashBoard: "",
          isTask: true,
          subtasks: Array.isArray(log.time_logs)
            ? log.time_logs.map((timeLog, index) => {
              const text = timeLog.notes;
              const clearNotes = document.createElement("div");
              clearNotes.innerHTML = text;
              const textNotes = clearNotes.innerText.trim();
              const timePeriod =
                timeLog.start_time == null || timeLog.end_time == null
                  ? ""
                  : `${convertTo12HourFormat(
                    timeLog.start_time
                  )} - ${convertTo12HourFormat(timeLog.end_time)}`;
              return {
                id: timeLog.time_logs_id, // Include unique id
                timeLogId: timeLog.time_logs_id, // Include time_logs_id
                user: timeLog.log_title,
                logHourse: timeLog.hours_in_text,
                Date: timeLog.log_date,
                timePeriod: timePeriod,
                dashBoard: textNotes,
                start_time: timeLog.start_time, // Add start_time
                end_time: timeLog.end_time, // Add end_time
                billingType: timeLog.billing_type,
                added_name: timeLog.added_name,
                isSubtask: true,
                project_title: timeLog.project_title,
                timesheet_type: timeLog.timesheet_type,
                reported_manager_id: timeLog.reported_manager_id,
                reported_manager_name: timeLog.reported_manager_name,
                approval_status: timeLog.approval_status,
                parentId: timeLog.project_id,
              };
            })
            : [],
        }));
      },
      providesTags: ["Timesheet"],
    }),
    fetchMailstoneTaskTimeSheetLogDetails: builder.query({
      query: (id) => `milestones/task/timelog/show/${id}`,
      transformResponse: (response) => {
        //console.log("get flaed slice  response :", response);
        return response.time_logs.map((log, index) => ({
          id: index,
          Date: log.log_date,
          userName: log.owner_name,
          logHourse: [
            `${log.total_hours}`,
            `${log.billable_hours}`,
            `${log.non_billable_hours}`,
          ],
          timePeriod: "",
          dashBoard: "",
          isTask: true,
          subtasks: Array.isArray(log.time_logs)
            ? log.time_logs.map((timeLog, index) => {
              const text = timeLog.notes;
              const clearNotes = document.createElement("div");
              clearNotes.innerHTML = text;
              const textNotes = clearNotes.innerText.trim();
              const timePeriod =
                timeLog.start_time == null || timeLog.end_time == null
                  ? ""
                  : `${convertTo12HourFormat(
                    timeLog.start_time
                  )} - ${convertTo12HourFormat(timeLog.end_time)}`;
              return {
                id: timeLog.time_logs_id, // Include unique id
                timeLogId: timeLog.time_logs_id, // Include time_logs_id
                user: timeLog.log_title,
                logHourse: timeLog.hours_in_text,
                Date: timeLog.log_date,
                timePeriod: timePeriod,
                dashBoard: textNotes,
                start_time: timeLog.start_time, // Add start_time
                end_time: timeLog.end_time, // Add end_time
                billingType: timeLog.billing_type,
                added_name: timeLog.added_name,
                isSubtask: true,
                project_title: timeLog.project_title,
                timesheet_type: timeLog.timesheet_type,
                reported_manager_id: timeLog.reported_manager_id,
                reported_manager_name: timeLog.reported_manager_name,
                approval_status: timeLog.approval_status,
                parentId: timeLog.project_id,
              };
            })
            : [],
        }));
      },
      providesTags: ["Timesheet"],
    }),
    fetchAdminTimeSheetLogDetails: builder.query({
      query: (id) => `getTimesheetDetails/${id}`,
      transformResponse: (response) => {
        if (!response.data) return [];

        // Separate structured projects and unstructured projects
        const structuredProjects = response.data.filter((item) =>
          Array.isArray(item)
        );
        const unstructuredProjects = response.data.filter(
          (item) => !Array.isArray(item)
        );

        // Flatten and process structured project logs
        const flatLogs = structuredProjects.flat();

        const structuredMapped = flatLogs.map((project) => ({
          id: `proj-${project.project_id}`,
          projectId: project.project_id,
          projectTitle: project.project_title,
          logHourse: [
            `${project.total_hours || 0} :00`,
            `${project.billable_hours || 0} :00`,
            `${project.non_billable_hours || 0} :00`,
          ],
          isTask: true,
          subtasks: Array.isArray(project.time_logs)
            ? project.time_logs.map((timeLog) => ({
              id: `log-${timeLog.time_logs_id}`,
              parentId: `proj-${project.project_id}`,
              user: timeLog.added_name || "Unknown",
              logHourse: timeLog.hours_in_text,
              timePeriod:
                timeLog.start_time && timeLog.end_time
                  ? `${convertTo12HourFormat(
                    timeLog.start_time
                  )} - ${convertTo12HourFormat(timeLog.end_time)}`
                  : "",
              dashBoard: new DOMParser()
                .parseFromString(timeLog.notes, "text/html")
                .body.textContent.trim(),
              billingType: timeLog.billing_type,
              added_name: timeLog.added_name,
              isSubtask: true,
            }))
            : [],
        }));

        // Process unstructured projects (those without `project_id`)
        const unstructuredMapped = unstructuredProjects.map((item, index) => ({
          id: `proj-placeholder-${index}`,
          projectId: `placeholder-${index}`,
          projectTitle: item.project_name || "Unnamed Project",
          logHourse: ["No TimeLogs in this project"],
          isTask: true,
          subtasks: [], // No subtasks available
        }));

        return [...structuredMapped, ...unstructuredMapped]; // Combine both lists
      },
    }),

    fetchBudgetPayments: builder.query({
      query: (id) => `projects/budget/viewAllPayment/${id}`,
      transformResponse: (response) => {
        const res = response.payments;
        //console.log("budget_response :", res);
        return response;
      },
    }),
    updateBudgetPayment: builder.mutation({
      query: ({ project_id, payment_id, data }) => ({
        url: `projects/budget/updatePayment/${project_id}/${payment_id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ["BudgetOverview"],
    }),
    deleteBudgetPayment: builder.mutation({
      query: ({ project_id, payment_id }) => ({
        url: `projects/budget/deletePayment/${project_id}/${payment_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ["BudgetOverview"],
    }),
    fetchClientsShowUsers: builder.query({
      query: (id) => `projects/client/show/${id}`,
      transformResponse: (response) => {
        // //console.log("client_show_users_response :", response);
        return response.data;
      },
    }),
    fetchPortalUser: builder.query({
      query: (id) => `projects/users/show/${id}`,
      transformResponse: (response) => {
        // //console.log("portal_user_response :", response);
        return response;
      },
    }),
    fetchClientCompanyName: builder.query({
      query: () => `getCompanyList`,
      transformResponse: (response) => {
        // //console.log("portal_user_response :", response);
        return response.data;
      },
    }),
    fetchBudgetSummaryTable: builder.query({
      query: (id) => `projects/budget/getSummary/${id}`,
      providesTags: ["AdditionalScope"],
      transformResponse: (response) => {
        if (response.data) {
          const formattedData = [
            {
              label: "Total Expenses",
              total_hours: response.data.total_hours,
              totalAmount: response.data.total_expenses,
            },
            {
              label: "Budget",
              total_hours: "",
              totalAmount: response.data.total_budget,
            },
            {
              label: "Profit",
              total_hours: "",
              totalAmount: response.data.profit,
              color: response.data.profit < 0 ? "red" : "inherit",
            },
            {
              label: " Profit %",
              total_hours: "",
              totalAmount: response.data.profit_percentage,
              color: response.data.profit_percentage.startsWith("-")
                ? "red"
                : "inherit",
            },
            {
              label: "Payments Received",
              total_hours: "",
              totalAmount: response.data.total_payments_received,
              payments_received: response.data.payments_received.map(
                (payment) => ({
                  added_at: payment.added_at,
                  amount: payment.amount,
                  name: payment.user_name,
                  invoice_number: payment.invoice_number,
                })
              ),
            },
            {
              label: "Payments Pending",
              total_hours: "",
              totalAmount: response.data.payments_pending,
            },
            {
              label: "% based on payment received",
              total_hours: "",
              totalAmount: response.data.payment_percentage_received,
              color: response.data.payment_percentage_received.startsWith("-")
                ? "red"
                : "inherit",
            },
          ];
          return formattedData;
        }
      },
    }),
    fetchBudgetExpenseTable: builder.query({
      query: (id) => `projects/budget/viewAllExpense/${id}`,
      transformResponse: (response) => {
        return response;
      },
    }),
    fetchBudgetAdditionalScopes: builder.query({
      query: (project_id) => `projects/budget/viewAllAdditionalScope/${project_id}`,
      providesTags: ["AdditionalScope"],
      transformResponse: (response) => {
        const raw = response?.data;
        // API returns { data: { items: [...], total_items, total_amount, total_amount_raw } }
        let items = [];
        if (Array.isArray(raw)) {
          items = raw;
        }
        else if (raw && Array.isArray(raw.items)) {
          items = raw.items;
        }
        else if (raw && Array.isArray(raw.scopes)) {
          items = raw.scopes;
        }
        else if (raw && Array.isArray(raw.additional_scopes)) {
          items = raw.additional_scopes;
        }
        // Map 'id' → 'scope_id' so edit/delete use the correct field
        return items.map((item) => ({
          ...item,
          scope_id: item.scope_id ?? item.id,
        }));
      },
    }),
    fetchBudgetOverview: builder.query({
      query: (id) => `projects/budget/getBudgetOverview/${id}`,
      // The Budget status bar. Anything that changes the money on the page invalidates
      // "BudgetOverview" so the bar updates without a page refresh.
      providesTags: ["AdditionalScope", "BudgetOverview"],
      transformResponse: (response) => response?.data,
    }),
    // arg: { id, basis } — basis "cost" returns each member's share of total
    // cost-to-company (manager/admin only); omitted/"hours" returns hours share.
    fetchTeamUtilisation: builder.query({
      query: ({ id, basis }) => ({
        url: `projects/budget/getTeamUtilisation/${id}`,
        params: basis === "cost" ? { basis: "cost" } : undefined,
      }),
      transformResponse: (response) => response?.data,
    }),

    // Add Additional Scope
    addAdditionalScope: builder.mutation({
      query: ({ project_id, payload }) => ({
        url: `projects/budget/addAdditionalScope/${project_id}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["AdditionalScope", "ProjectDetail"],
    }),

    // Update Additional Scope
    updateAdditionalScope: builder.mutation({
      query: ({ scope_id, payload }) => ({
        url: `projects/budget/updateAdditionalScope/${scope_id}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["AdditionalScope", "ProjectDetail"],
    }),

    // Delete Additional Scope (soft-delete)
    deleteAdditionalScope: builder.mutation({
      query: (scope_id) => ({
        url: `projects/budget/deleteAdditionalScope/${scope_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdditionalScope", "ProjectDetail"],
    }),
    fetchMilestoneNames: builder.query({
      query: (id) => `fetchMilestonesByProjectDetails/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneNames_list response_data :", response);

        return response.data;
      },
      providesTags: ["Milestone"],
    }),
    fetchMilestoneTasks: builder.query({
      query: (id) => `tasksInMilestones/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneTasks_list response_data :", response);

        return response.data;
      },
    }),
    fetchMilestoneIssues: builder.query({
      query: (id) => `issuesInMilestones/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneIssues_list response_data :", response);
        return response.data;
      },
    }),
    fetchGetAllProjectBasedUserNameList: builder.query({
      query: (id) => `usersInProjects/${id}`,
      transformResponse: (response) => {
        //console.log("projectBasedUsers_list response_data :", response);
        return response.data;
      },
    }),
    fetchGetAllTaskOrIssueBasedUserNameList: builder.query({
      query: (id) => `getTaskOrIssueOwners/${id}`,
      transformResponse: (response) => {
        //console.log("projectBasedUsers_list response_data :", response);
        return response.data;
      },
    }),
    fetchGetAllProjectBasedTaskNames: builder.query({
      query: (id) => `getUserAccessedTask/${id}`,
      transformResponse: (response) => {
        //console.log("projectBasedTsk_list response_data :", response);
        return response.data;
      },
    }),
    fetchGetAllProjectBasedIssueNames: builder.query({
      query: (id) => `getUserAccessedIssue/${id}`,
      transformResponse: (response) => {
        //console.log(
        //   " projectBasedTsk_list projectBasedIssue_list response_data :",
        //   response
        // );
        return response.data;
      },
    }),
    fetchViewTaskByID: builder.query({
      query: (id) => `task/view/${id}`,
      transformResponse: (response) => {
        //console.log("viewTaskByID response_data :", response);
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    fetchMasterAllStatus: builder.query({
      query: (id) => `master/status/show`,
      transformResponse: (response) => {
        //console.log("masterAllStatus response_data :", response);
        return response.data;
      },
    }),
    fetchMasterPriorityStatus: builder.query({
      query: (id) => `master/priority/show`,
      transformResponse: (response) => {
        //console.log("masterAllStatus response_data :", response);
        return response.data;
      },
    }),
    fetchMasterSeverityStatus: builder.query({
      query: (id) => `master/severity/show`,
      transformResponse: (response) => {
        //console.log("masterAllStatus response_data :", response);
        return response.data;
      },
    }),
    fetchMasterRole: builder.query({
      query: () => `master/designation/show`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchMasterSkills: builder.query({
      query: () => `master/skills/show`,
      transformResponse: (response) => {
        return response.data;
      },
    }),

    fetchMasterDepartment: builder.query({
      query: (id) => `master/department/show`,
      transformResponse: (response) => {
        return response.data;
      },
    }),

    fetchGetAllMasterDesignations: builder.query({
      query: () => `getDesignationList`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchPersonalSettings: builder.query({
      query: (id) => `settings/profile/get`,
      transformResponse: (response) => {
        //console.log("personalSettings response_data :", response);
        return response.data;
      },
    }),
    fetchShowAllProjects: builder.query({
      query: (filter) => `projects/show/${filter}`,
      transformResponse: (response) => {
        //console.log("project_api_view response_data  slice file:", response);
        return response;
      },
    }),
    fetchViewProjectsById: builder.query({
      query: (id) => `projects/${id}`,
      transformResponse: (response) => {
        //console.log("viewProjectsById response_data :", response);
        return response.data;
      },
      providesTags: ["ProjectDetail"],
    }),
    fetchTaskLists: builder.query({
      query: (id) => `tasklistDetails/${id}`,
      transformResponse: (response) => {
        //console.log("tasklistDetails response_data :", response);
        return response.data;
      },
    }),
    fetchTaskNameById: builder.query({
      query: (id) => `tasksInProjects/${id}`,
      transformResponse: (response) => {
        //console.log("taskNameById response_data :", response);
        return response.data;
      },
    }),
    fetchTaskSlider: builder.query({
      query: (id) => `tasklist/show`,
      transformResponse: (response) => {
        //console.log("taskNameById response_data :", response);
        return response.data;
      },
    }),
    fetchTaskDetailView: builder.query({
      query: (id) => `task/view/${id}`,
      transformResponse: (response) => {
        //console.log("taskDetailView response_data :", response);
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    fetchIssueDetailView: builder.query({
      query: (id) => `issue/view/${id}`,
      transformResponse: (response) => {
        //console.log("taskDetailView response_data :", response);
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    // activity
    fetchTaskActivity: builder.query({
      query: (id) => `task/activity/${id}`,
      transformResponse: (response) => {
        //console.log("taskActivity response_data :", response);
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    fetchIssueActivity: builder.query({
      query: (id) => `issue/activity/${id}`,
      transformResponse: (response) => {
        //console.log("taskActivity response_data :", response);
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    fetchProjectActivity: builder.query({
      query: (id) => `projects/activity/${id}`,
      transformResponse: (response) => {
        //console.log("taskActivity response_data :", response);
        return response.data;
      },
    }),
    fetchMilestoneActivity: builder.query({
      query: (id) => `milestones/activity/${id}`,
      transformResponse: (response) => {
        //console.log("taskActivity response_data :", response);
        return response.data;
      },
    }),
    fetchMilestoneTaskActivity: builder.query({
      query: (id) => `milestones/task/activity/${id}`,
      transformResponse: (response) => {
        //console.log("taskActivity response_data :", response);
        return response.data;
      },
    }),
    fetchMilestoneIssueActivity: builder.query({
      query: (id) => `milestones/issue/activity/${id}`,
      transformResponse: (response) => {
        //console.log("taskActivity response_data :", response);
        return response.data;
      },
    }),
    // comments
    fetchGetallProjetsComments: builder.query({
      query: (id) => `projects/comments/${id}`,
      transformResponse: (response) => {
        //console.log("getAllProjetsComments response_data :", response);
        return response.comment;
      },
    }),
    fetchGetallTaskComments: builder.query({
      query: (id) => `task/comments/${id}`,
      transformResponse: (response) => {
        //console.log("getAllProjetsComments response_data :", response);
        return response.comment;
      },
    }),
    fetchGetallIssueComments: builder.query({
      query: (id) => `issue/comments/${id}`,
      transformResponse: (response) => {
        //console.log("getAllProjetsComments response_data :", response);
        return response.comment;
      },
    }),
    fetchGetallMilestoneComments: builder.query({
      query: (id) => `milestones/comments/${id}`,
      transformResponse: (response) => {
        //console.log("getAllProjetsComments response_data :", response.comment);
        return response.comment;
      },
    }),
    fetchGetallMilestoneTaskComments: builder.query({
      query: (id) => `milestones/task/comments/${id}`,
      transformResponse: (response) => {
        //console.log("getAllProjetsComments response_data :", response.comment);
        return response.comment;
      },
    }),
    fetchGetallMilestoneIssueComments: builder.query({
      query: (id) => `issue/comments/${id}`,
      transformResponse: (response) => {
        //console.log("getAllProjetsComments response_data :", response.comment);
        return response.comment;
      },
    }),
    // milestone task
    fetchMilestoneShowTasks: builder.query({
      query: (id) => `milestones/task/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneShowTasks response_data :", response);
        return response.data;
      },
    }),
    fetchMilestoneDetails: builder.query({
      query: (id) => `milestones/tasklist/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneShowTasks response_data :", response);
        return response;
      },
    }),
    fetchMilestoneTaskView: builder.query({
      query: (id) => `milestones/task/view/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneTaskView response_data :", response);
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    // milestone issue
    fetchMilestoneIsssueView: builder.query({
      query: (id) => `milestones/issue/view/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneIssueView response_data :", response);
        return response.data;
      },
    }),
    fetchMilestoneBasedIsuueFilter: builder.query({
      query: (id) => `issuesInMilestones/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneIssueView response_data :", response);
        return response.data;
      },
    }),
    fetchProjectBasedIsuueFilter: builder.query({
      query: (id) => `issuesInProjects/${id}`,
      transformResponse: (response) => {
        //console.log("milestoneIssueView response_data :", response);
        return response.data;
      },
    }),
    // rate per hour
    // fetchRatePerHour: builder.query({
    //   query: (id) => `ratePerHour/show?search=""`,
    //   transformResponse: (response) => {
    //     //console.log("rate_per_hour  response_data :", response.data);
    //     return response.data;
    //   },
    // }),

    fetchRatePerHour: builder.query({
      query: ({ search = "" } = {}) => {
        const params = {
          search,
          //  view_type_id: view_type_id ?? 1,
        };
        return {
          url: `ratePerHour/show`,
          params,
        };
      },
      transformResponse: (response) => {
        //console.log("module_route task slice:", response);

        return response.data;
      },
    }),

    // get all users
    fetchGetallUserList: builder.query({
      query: (id) => `getAllUserDetails`,
      transformResponse: (response) => {
        //console.log("getAllUserDetails  response_data :", response.data);
        return response.data;
      },
    }),
    fetchGetallClientList: builder.query({
      query: (id) => `getAllClientDetails`,

      transformResponse: (response) => {
        //console.log("getAllUserDetails  response_data :", response.data);
        return response.data;
      },
    }),
    fetchClientDetails: builder.query({
      query: (id) => `clientDetails`,

      transformResponse: (response) => {
        //console.log("getAllUserDetails  response_data :", response.data);
        return response.data;
      },
    }),
    // portal user add
    fetchAddportalUser: builder.query({
      query: ({ id, type }) => `usersListToAddProjects/${id}?type=${type}`,
      transformResponse: (response) => {
        //console.log("usersListToAddProject  response_data :", response.data);
        return response.data;
      },
    }),
    // timesheet detail page
    fetchTimesheetDetail: builder.query({
      query: (id) => `timesheet/view/${id}`,
      transformResponse: (response) => {
        //console.log("timesheetDetail  response_data :", response.data);
        return response.data;
      },
    }),
    // dashBoard
    fetchDashboardDetails: builder.query({
      query: (id) => `dashboard`,
      transformResponse: (response) => {
        //console.log("dashboardUserDetails  response_data :", response.data);
        return response.data;
      },
    }),
    fetchDashboardProjectDetails: builder.query({
      query: (id) => `dashboard/projects`,
      transformResponse: (response) => {
        //console.log(
        //   "dashboardProjectDetails  response_data :",
        //   response.recentProjects
        // );
        return response.recentProjects;
      },
    }),
    fetchDashboardTaskDetails: builder.query({
      query: (id) => `dashboard/tasks`,
      transformResponse: (response) => {
        //console.log("dashboardTaskDetails  response_data :", response.myTasks);
        return response.myTasks;
      },
    }),
    fetchDashboardIsuueDetails: builder.query({
      query: (id) => `dashboard/issues`,
      transformResponse: (response) => {
        return response.myIssues;
      },
    }),
    fetchDashboardTodaysTask: builder.query({
      query: ({ date }) => {
        const params = {
          date: date,
        };
        return {
          url: `dashboard/taskAllocations`,
          params: params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchMonthlyReports: builder.query({
      query: ({ date, search = "" }) => {
        const params = {
          month_year: date,
          search,
        };
        return {
          url: `getMonthlyReport`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchMyOverdues: builder.query({
      query: (id) => `dashboard/overdue`,
      transformResponse: (response) => {
        //console.log(
        //   "dashboardMilestoneDetails  response_data :",
        //   response.overdueTasks
        // );
        return response.overdueTasks;
      },
    }),
    fetchDashboardTimesheetDetails: builder.query({
      query: (params) => `dashboard/timesheets?${params}`,
      transformResponse: (response) => {
        //console.log(
        //   "dashboardTimesheetDetails  response_data :",
        //   response.timesheet
        // );
        return response.timesheet;
      },
    }),

    fetchUserInfo: builder.query({
      query: (id) => `dashboard/user-info`,
      transformResponse: (response) => {
        //console.log("dashboardActivityStream  response_data :", response.user);
        return response.user;
      },
    }),
    // notification
    fetchNotificationList: builder.query({
      query: (id) => `notification/show/all`,
      transformResponse: (response) => {
        //console.log(
        //   "fetchNotificationList  response_data :",
        //   response.notifications
        // );
        return response.notifications;
      },
    }),
    // timesheet detail show
    fetchTimesheetNames: builder.query({
      // query: (id) => `timesheet/getlists/${id}`,
      query: ({ project_id, user_id }) => {
        const params = {
          project_id,
          user_id,
        };
        return {
          url: `timesheet/getlists`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.time_logs;
      },
      providesTags: ["Timesheet"],
    }),
    // get all projects
    fetchGetallProjects: builder.query({
      query: () => `getAllProjectsDetails`,
      transformResponse: (response) => {
        //console.log("user_project", response);

        return response.data;
      },
    }),

    // get all user with role based
    getAllUserWithRole: builder.query({
      query: () => `getAllUsersListsWithRoleBased`,
      transformResponse: (response) => {
        return response.data;
      }
    }),

    /*
     fetchMasterDepartment: builder.query({
     query: () => `master/department/show`,
     transformResponse: (response) => {
     return response.data;
  },
}),
*/


    // get department users

    // getDepartmentUsersLists : builder.query({
    //   query :({department_ids})=>{
    //     const params = {department_ids};
    //     return{
    //       url:`getDepartmentUsers`,
    //       params,
    //     }
    //   } ,
    //   transformResponse :(response) => {
    //     return response.data;
    //   }
    // }),

    // Update your getDepartmentUsers endpoint in logdetailSlice.js
    getDepartmentUsers: builder.query({
      query: (data) => {
        // Create URLSearchParams with array bracket notation
        const params = new URLSearchParams();
        data.department_ids.forEach(id => {
          params.append('department_ids[]', id);
        });

        return {
          url: `getDepartmentUsers?${params.toString()}`,
          method: 'GET',
        };
      },
    }),


    // task module
    fetchTaskModule: builder.query({
      query: () => `tasks/show/all`,
      transformResponse: (response) => {
        //console.log("module_route task slice:", response);

        return response.data;
      },
    }),
    fetchIssueModule: builder.query({
      query: ({ searchText = "", view_type_id } = {}) => {
        const params = {
          searchText,
          view_type_id: view_type_id ?? 1,
        };
        return {
          url: `issues/show/all`,
          params,
        };
      },
      transformResponse: (response) => {
        //console.log("module_route task slice:", response);

        return response.data;
      },
    }),
    fetchMilestoneModule: builder.query({
      query: ({ searchText = "", view_type_id }) => {
        const params = {
          searchText,
          view_type_id: view_type_id ?? 1,
        };
        return {
          url: `milestone/show/all`,
          params,
        };
      },
      transformResponse: (response) => {
        //console.log("module_route task slice:", response);

        return response.data;
      },
      providesTags: ["Milestone"],
    }),
    // get task remainder users= user group data
    fetchTaskRemainderUsers: builder.query({
      query: ({ type, id }) => {
        const params = {
          type: type,
          id: id,
        };
        return {
          url: `user-groups`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data.project_users;
      },
    }),
    fetchBenchSummary: builder.query({
      query: ({ date }) => {
        const params = {
          date: date,
        };
        return {
          url: `task-allocation/work-status-summary`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // user accesssed datas
    fetchUserAccessedProjects: builder.query({
      query: () => `getUserAccessedProject`,
      transformResponse: (response) => {
        //console.log("getUserAccessedProject in slice :", response);

        return response.data;
      },
    }),

    fetchAllDepartments: builder.query({
      query: () => `getAllDepartments/${true}`,
      transformResponse: (response) => {
        //console.log("getUserAccessedProject in slice :", response);

        return response.data;
      },
    }),

    fetchWorkStatus: builder.query({
      query: ({
        date,
        department_id,
        search,
        work_status,
        export: is_export,
      }) => {
        const params = {
          date,
          department_id,
          search,
          work_status,
          export: is_export,
        };

        return {
          url: "task-allocation/work-status",
          params,
        };
      },
      transformResponse: (response) => {
        return {
          data: response?.data || [],
          meta: response?.meta || {},
        };
      },
    }),

    fetchTimesheetModule: builder.query({
      // query: ({ project_span, from_date, to_date, view_type }) =>
      //   `timesheets/show/all?project_span=${project_span}&from_date=${from_date}&to_date=${to_date}&view_type=${
      //     view_type ?? 6
      //   }`,
      query: ({ project_span, from_date, to_date, view_type, group_by }) => {
        const params = {
          project_span,
          from_date,
          to_date,
          view_type: view_type ?? 6,
          groupBy: group_by,
        };
        return {
          url: `timesheets/show/all`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.time_logs.map((log, index) => ({
          id: index,
          Date: log.log_date,
          userName: log.group_by,
          logHourse: [
            `${log.total_hours}`,
            `${log.billable_hours}`,
            `${log.non_billable_hours}`,
          ],
          timePeriod: "",
          dashBoard: "",
          isTask: true,
          isSubtask: false,
          subtasks: Array.isArray(log.time_logs)
            ? log.time_logs.map((timeLog) => {
              // const clearNotes = document.createElement("div");
              // clearNotes.innerHTML = timeLog.notes;
              // const textNotes = clearNotes.innerText.trim();
              const timePeriod =
                timeLog.start_time == null || timeLog.end_time == null
                  ? ""
                  : `${convertTo12HourFormat(
                    timeLog.start_time
                  )} - ${convertTo12HourFormat(timeLog.end_time)}`;

              return {
                id: timeLog.time_logs_id,
                timeLogId: timeLog.time_logs_id,
                user: timeLog.log_title,
                owner_id: timeLog.user_id,
                other_log_entries: timeLog.other_log_entries,
                logHourse: timeLog.hours_in_text,
                Date: timeLog.log_date,
                timePeriod: timePeriod,
                ownerName: timeLog.owner_name,
                dashBoard: timeLog.notes,
                start_time: timeLog.start_time,
                end_time: timeLog.end_time,
                project_title: timeLog.project_title,
                timesheet_type: timeLog.timesheet_type,
                reported_manager_name: timeLog.reported_manager_name,
                approval_status: timeLog.approval_status,
                billingType: timeLog.billing_type,
                task_id: timeLog.task_id,
                issue_id: timeLog.issue_id,
                added_name: timeLog.added_name,
                reported_manager_id: timeLog.reported_manager_id,
                reported_manager_name: timeLog.reported_manager_name,
                milestone: timeLog.milestone,
                isSubtask: true,
                parentId: timeLog.project_id,
              };
            })
            : [],
        }));
      },
      providesTags: ["Timesheet", "Milestone"],
    }),
    fetchTotalLogHours: builder.query({
      query: ({ project_span, from_date, to_date, view_type, group_by }) => {
        const params = {
          project_span,
          from_date,
          to_date,
          groupBy: group_by,
          view_type: view_type ?? 6,
        };
        return {
          url: `timesheets/show/all`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.total_time_logs_hours;
      },
      providesTags: ["Timesheet", "Milestone"],
    }),
    fetchTotalLogHoursInProjectTab: builder.query({
      query: ({
        id,
        project_span,
        from_date,
        to_date,
        view_type,
        group_by,
      }) => {
        const params = {
          project_span,
          from_date,
          to_date,
          view_type: view_type ?? 6,
          groupBy: group_by,
        };
        return {
          url: `timesheet/show/${id}`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.total_time_logs_hours;
      },
      providesTags: ["Timesheet", "Milestone"],
    }),
    fetchuserbasedTasks: builder.query({
      query: ({ user_id, project_id }) => {
        const params = {
          user_id,
          project_id,
        };
        return {
          url: `getAccessedTaskByUserId`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchTaskAllocation: builder.query({
      query: ({ date, department_id, search = "", isExport }) => {
        const params = {
          date,
          department_id: department_id ?? "",
          search,
          isExport,
        };
        return {
          url: `task-allocation/view`,
          params,
        };
      },
      transformResponse: (response) => {
        return response;
      },
      // transformResponse: (response) => {
      //   return {
      //     data: response.data ?? [],
      //     meta: response.meta ?? {},
      //   };
      // },
    }),
    fetchuserbasedIssues: builder.query({
      query: ({ user_id, project_id }) => {
        const params = {
          user_id,
          project_id,
        };
        return {
          url: `getAccessedIssueByUserId`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchPasswordRecomment: builder.query({
      query: () => `show-password-expiry-notification`,
      transformResponse: (response) => {
        return response.notifications;
      },
    }),
    fetchGetallDepartments: builder.query({
      query: () => `getAllDepartments`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchGetAllSkills: builder.query({
      query: () => `getAllSkills`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    // fetchDuplicateTaskAllocation: builder.query({
    //   query: () => `task-allocation/duplicate-preview`,
    //   transformResponse: (response) => {
    //     return response.data;
    //   },
    // }),

    fetchDuplicateTaskAllocation: builder.query({
      query: ({ original_date, new_date, search }) => {
        const params = {
          original_date,
          new_date,
          search,
        };
        return {
          url: `task-allocation/duplicate-preview`,
          params,
        };
      },
      transformResponse: (response) => {
        return response;
      },
    }),

    fetchTimerActivities: builder.query({
      query: (id) => `timer/getTimerActivityStream/${id}`,
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    fetchGetallManagers: builder.query({
      query: (id) => `getManagerName`,
      transformResponse: (response) => {
        return response.owner_name;
      },
    }),
    fetchGetManagerByProject: builder.query({
      query: (id) => `getProjectManager/${id}`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchTimeLogActivities: builder.query({
      query: (id) => `timesheet/getTimeLogActivityStream/${id}`,
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: ["Timesheet"],
    }),
    fetchGetallDrafts: builder.query({
      query: ({ date }) => {
        const params = {
          date,
        };
        return {
          url: `task-allocation/drafts/get`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchAllAppraisalFeedback: builder.query({
      query: ({ feedback_type, search }) => {
        const params = {
          feedback_type: feedback_type,
          search: search,
        };
        return {
          url: `appraisalFeedback/getAppraisalFeedbacks`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchMonthlyLogs: builder.query({
      query: () => `actionLogs`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    fetchProjectList: builder.query({
      query: () => `projects/show`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    // Fetch previous invoices for a project (used in FixedCostMappingModal)
    fetchPreviousInvoices: builder.mutation({
      query: (payload) => ({
        url: `invoices/previous-invoices`,
        method: 'POST',
        body: payload,
      }),
    }),
    fetchProjectbasedTasklist: builder.query({
      query: (project_id) => `getTasklistName/${project_id}`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
    // OT SHEET PENDING
    fetchOTLists: builder.query({
      query: (params) => {
        return {
          url: `ot/getAllOts`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),
    // OT Reports
    fetchOTReports: builder.query({
      query: (params) => {
        return {
          url: `ot/reports`,
          params,
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // issue module dropdown
    fetchIssueModuleDropdown: builder.query({
      query: (project_id) => `getIssueModuleName/${project_id}`,
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // post methods
    fetchExportUsers: builder.mutation({
      query: ({ url, body }) => ({
        url: url,
        body: body,
        method: "POST",
      }),
    }),
    fetchCreateMasterRoles: builder.mutation({
      query: ({ url, body }) => ({
        url: url,
        body: body,
        method: "POST",
      }),
    }),
    fetchCreateMasterDepartments: builder.mutation({
      query: ({ url, body }) => ({
        url: url,
        body: body,
        method: "POST",
      }),
    }),
    fetchCreateClientTestimonial: builder.mutation({
      query: ({ url, body }) => ({
        url: url,
        body: body,
        method: "POST",
      }),
    }),
    fetchGenerateTestimonialLink: builder.mutation({
      query: ({ url, id }) => ({
        url: `${url}/${id}`,
        method: "POST",
      }),
    }),
    DeleteTestimonial: builder.mutation({
      query: (id) => ({
        url: `testimonial/${id}`,
        method: "DELETE",
      }),
    }),
    // fetchCreateMasterSkills: builder.mutation({
    //   query: ({ url, body }) => ({
    //     url
    //   })
    // }),

    fetchCreateDatas: builder.mutation({
      query: ({ url, body }) => ({
        url: url,
        body: body,
        method: "POST",
      }),
    }),



    // task remainder

    fetchCreateRemainder: builder.mutation({
      query: ({ url, data }) => ({
        url: url,
        body: data,
        method: "POST",
      }),
    }),
    // task recurrence
    fetchCreatereccurence: builder.mutation({
      query: ({ url, data }) => ({
        url: url,
        body: data,
        method: "POST",
      }),
    }),
    // task allocation
    fetchCreateOrUpdateTaskAllocation: builder.mutation({
      query: ({ path, data }) => ({
        url: path,
        body: data,
        method: "POST",
      }),
    }),
    // create rateper hour
    fetchUpdateRatePerHour: builder.mutation({
      query: ({ data, id }) => ({
        url: `ratePerHour/update/${id}`,
        body: data,
        method: "POST",
      }),
    }),
    // In your API slice
    fetchDeleteRatePerHour: builder.mutation({
      query: (id) => ({
        url: `ratePerHour/delete/${id}`,
        method: "DELETE",
      }),
    }),
    updateDarkMode: builder.mutation({
      query: (darkMode) => ({
        url: "users/changeDarkMode",
        method: "POST",
        body: { dark_mode: darkMode },
      }),
    }),

    // Workforce stability 
    generateWorkforceReport: builder.mutation({
      query: (data) => ({
        url: 'workforce-stability/generateReport',
        method: 'POST',
        body: data
      }),
      transformResponse: (response) => {
        return response.data
      },
    }),

    // Create Awards
    createAward: builder.mutation({
      query: (data) => ({
        url: "awards",
        method: "POST",
        body: data,
        headers: {
          Accept: "application/json",
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // Fetch Certificates
    fetchCertificates: builder.query({
      query: (params) => ({
        url: "awards",
        params: params,
        headers: {
          Accept: "application/json",
        },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
    }),

    // Update Award
    updateAward: builder.mutation({
      query: (data) => ({
        url: `awards/${data.award_employee_id}`,
        method: "POST",
        body: data,
        headers: {
          Accept: "application/json",
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // Delete Award
    deleteAward: builder.mutation({
      query: (data) => ({
        url: `awards/${data.award_employee_id}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // Download Certificate
    downloadCertificate: builder.query({
      query: (data) => ({
        url: `awards/certificate/${data.certificate_id}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
    }),

    // Verify Certificate API
    verifyCertificate: builder.query({
      query: (token) => ({
        url: `awards/verify-certificate/${token}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // Zoho Sync Invoices (Resource Earning Module)
    syncZohoInvoices: builder.mutation({
      query: (date) => ({
        url: date ? `zoho/sync-invoices?date=${date}` : `zoho/sync-invoices`,
        method: "GET",
      }),
      invalidatesTags: ["InvoicesQueue", "InvoicesSources"],
    }),

    // Invoices Queue List (Resource Earning Module)
    fetchInvoicesQueue: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append("search", params.search);
        if (params?.source && params.source !== "All sources") queryParams.append("source", params.source);
        if (params?.status && params.status !== "All statuses") queryParams.append("status", params.status.toLowerCase());
        if (params?.from_date) queryParams.append("from_date", params.from_date);
        if (params?.to_date) queryParams.append("to_date", params.to_date);
        const queryStr = queryParams.toString();
        return queryStr ? `invoices?${queryStr}` : "invoices";
      },
      providesTags: ["InvoicesQueue"],
    }),

    // Invoice Sources Dropdown List (Resource Earning Module)
    fetchInvoiceSources: builder.query({
      query: () => "invoices/sources",
      providesTags: ["InvoicesSources"],
    }),

    // Invoice Projects List (Resource Earning Module)
    fetchInvoiceProjects: builder.query({
      query: () => "invoices/projects",
      providesTags: ["InvoiceProjects"],
    }),

    // Parse Invoice Resources (Contract Mapping)
    parseInvoiceResources: builder.mutation({
      query: (body) => ({
        url: "invoices/parse-resources",
        method: "POST",
        body,
      }),
    }),

    // Allocate Invoice (Contract Mapping / Resource Earning Module)
    allocateInvoice: builder.mutation({
      query: (body) => ({
        url: "invoices/allocate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["InvoicesQueue", "InvoiceProjects"],
    }),

    // Fetch Invoice By ID (Resource Earning Module)
    fetchInvoiceById: builder.query({
      query: (invoiceId) => `invoices/${invoiceId}`,
      providesTags: (result, error, id) => [{ type: "InvoiceDetail", id }],
    }),

    // Create Manual Invoice (Resource Earning Module)
    createManualInvoice: builder.mutation({
      query: (body) => ({
        url: "manual-invoice/create",
        method: "POST",
        body,
      }),
      invalidatesTags: ["InvoicesQueue", "InvoiceProjects", "InvoiceReferenceId"],
    }),

    // Delete Manual Invoice (Resource Earning Module)
    deleteManualInvoice: builder.mutation({
      query: (id) => ({
        url: `manual-invoice/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["InvoicesQueue", "InvoiceProjects", "InvoiceReferenceId"],
    }),

    // Fetch Next Manual Invoice Reference ID (Resource Earning Module)
    fetchInvoiceReferenceId: builder.query({
      query: () => "invoices/reference_id",
      providesTags: ["InvoiceReferenceId"],
    }),

    // Fetch Project Resources for Manual/Contract Allocation (Resource Earning Module)
    fetchProjectResources: builder.mutation({
      query: (body) => ({
        url: "invoices/project-resources",
        method: "POST",
        body,
      }),
    }),

    // Allocate Contract Invoice (Manual Contract / Resource Earning Module)
    allocateContractInvoice: builder.mutation({
      query: (body) => ({
        url: "invoices/allocate-contract",
        method: "POST",
        body,
      }),
      invalidatesTags: ["InvoicesQueue", "InvoiceProjects"],
    }),

    // Allocate Fixed Cost Invoice (Resource Earning Module)
    allocateFixedCostInvoice: builder.mutation({
      query: (body) => ({
        url: "invoices/allocate-fixed-cost",
        method: "POST",
        body,
      }),
      invalidatesTags: ["InvoicesQueue", "InvoiceProjects"],
    }),

    // Map Fixed Cost Invoice to Project (Resource Earning Module)
    fixedCostMapInvoice: builder.mutation({
      query: (body) => ({
        url: "invoices/map",
        method: "POST",
        body,
      }),
      invalidatesTags: ["InvoicesQueue", "InvoiceProjects"],
    }),

    // Fetch Resource Earnings (Resource Earning Module - Resource Invoice)
    fetchResourceEarnings: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.user_id) queryParams.append("user_id", params.user_id);
        if (params.from_date) queryParams.append("from_date", params.from_date);
        if (params.to_date) queryParams.append("to_date", params.to_date);
        const qStr = queryParams.toString();
        return `invoices/resource-earnings${qStr ? `?${qStr}` : ""}`;
      },
      providesTags: ["ResourceEarnings"],
    }),

    // Export Resource Earnings (Resource Earning Module - Resource Invoice Export)
    exportResourceEarnings: builder.mutation({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.user_id) queryParams.append("user_id", params.user_id);
        if (params.from_date) queryParams.append("from_date", params.from_date);
        if (params.to_date) queryParams.append("to_date", params.to_date);
        const qStr = queryParams.toString();
        return {
          url: `invoices/resource-earnings/export${qStr ? `?${qStr}` : ""}`,
          method: "GET",
        };
      },
    }),

    // Fetch Awaiting Allocation (Resource Earning Module - Tab 3)
    fetchAwaitingAllocation: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status && params.status !== "All statuses") queryParams.append("status", params.status);
        if (params.project_id && params.project_id !== "All projects") queryParams.append("project_id", params.project_id);
        if (params.model && params.model !== "All models") queryParams.append("model", params.model);
        if (params.source && params.source !== "All sources") queryParams.append("source", params.source);
        if (params.age && params.age !== "All ages") queryParams.append("age", params.age);
        const qStr = queryParams.toString();
        return `invoices/awaiting-allocation${qStr ? `?${qStr}` : ""}`;
      },
      providesTags: ["AwaitingAllocation"],
    }),

    // Export Awaiting Allocation (Resource Earning Module - Tab 3)
    exportAwaitingAllocation: builder.mutation({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status && params.status !== "All statuses") queryParams.append("status", params.status);
        if (params.project_id && params.project_id !== "All projects") queryParams.append("project_id", params.project_id);
        if (params.model && params.model !== "All models") queryParams.append("model", params.model);
        if (params.source && params.source !== "All sources") queryParams.append("source", params.source);
        if (params.age && params.age !== "All ages") queryParams.append("age", params.age);
        const qStr = queryParams.toString();
        return {
          url: `invoices/awaiting-allocation/export${qStr ? `?${qStr}` : ""}`,
          method: "GET",
        };
      },
    }),

    // Fetch Project Earnings (Fixed Cost Project Earnings API)
    fetchProjectEarnings: builder.query({
      query: (arg = {}) => {
        const queryParams = new URLSearchParams();
        const project_id = typeof arg === "object" && arg !== null ? arg?.project_id : arg;
        const scope_id = typeof arg === "object" && arg !== null ? (arg?.scope_id ?? "") : "";

        if (project_id) queryParams.append("project_id", project_id);
        if (scope_id !== undefined && scope_id !== null) queryParams.append("scope_id", scope_id);
        const qStr = queryParams.toString();
        return `invoices/project-earnings${qStr ? `?${qStr}` : ""}`;
      },
      providesTags: ["ProjectDetail", "ResourceEarnings"],
    }),

  }),
});





// apiAppraisalFeedback url:
//------------------------------

const baseQuery = withCsrfFailureMessage(
  fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include", // MUST include this to send the HttpOnly pms_session cookie
    prepareHeaders: (headers) => {
      // Intentionally not setting Authorization header
      // The backend validates the pms_session cookie; mutating requests must
      // also echo the XSRF-TOKEN cookie in the X-XSRF-TOKEN header.
      return applyCsrfHeader(headers);
    },
  })
);

export const logdetailSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQuery,
  tagTypes: ["AppraisalFeedback", "Projects", "Users", "Notifications"],
  endpoints: (builder) => ({
    // Existing endpoints...
    useFetchGetallProjectsQuery: builder.query({
      query: () => "/projects",
      providesTags: ["Projects"],
    }),

    useFetchUserAccessedProjectsQuery: builder.query({
      query: () => "/projects/user-accessed",
      providesTags: ["Projects"],
    }),

    // ============================================
    // NEW: Appraisal Feedback Endpoints
    // ============================================
    useFetchAppraisalFeedbacksQuery: builder.query({
      query: (params) => ({
        url: "/appraisal-feedbacks",
        params,
      }),
      providesTags: ["AppraisalFeedback"],
    }),

    useFetchAppraisalFeedbackByIdQuery: builder.query({
      query: (id) => `/appraisal-feedbacks/${id}`,
      providesTags: (result, error, id) => [{ type: "AppraisalFeedback", id }],
    }),

    useCreateAppraisalFeedbackMutation: builder.mutation({
      query: (data) => ({
        url: "/appraisal-feedbacks",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["AppraisalFeedback"],
    }),

    useUpdateAppraisalFeedbackMutation: builder.mutation({
      query: ({ id, data }) => ({
        url: `/appraisal-feedbacks/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "AppraisalFeedback",
        { type: "AppraisalFeedback", id },
      ],
    }),

    useDeleteAppraisalFeedbackMutation: builder.mutation({
      query: (id) => ({
        url: `/appraisal-feedbacks/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AppraisalFeedback"],
    }),

    useSearchAppraisalFeedbacksMutation: builder.mutation({
      query: (searchData) => ({
        url: "/appraisal-feedbacks/search",
        method: "POST",
        body: searchData,
      }),
    }),

    useFilterAppraisalFeedbacksMutation: builder.mutation({
      query: (filterData) => ({
        url: "/appraisal-feedbacks/filter",
        method: "POST",
        body: filterData,
      }),
    }),

    // Other existing endpoints...
    useFetchNotificationListQuery: builder.query({
      query: () => "/notifications",
      providesTags: ["Notifications"],
    }),

    useFetchClientDetailsQuery: builder.query({
      query: () => "/clients",
      providesTags: ["Users"],
    }),

    useFetchGetAllProjectBasedUserNameListQuery: builder.query({
      query: () => "/users/project-based",
      providesTags: ["Users"],
    }),

    // Award Periods (added 07-03-2026)
    fetchAwardPeriods: builder.query({
      query: () => `master/award-periods/show`,
      transformResponse: (response) => {
        return response.data;
      },
    }),
  }),
});

// Export hooks for use in components
export const {
  // timesheet
  useFetchLogDetailsQuery,
  useFetchTimeSheetLogDetailsQuery,
  useFetchIssueTimeSheetLogDetailsQuery,
  useFetchMailstoneTimeSheetLogDetailsQuery,
  useFetchAdminTimeSheetLogDetailsQuery,
  useFetchTotalLogHoursInProjectTabQuery,
  // timesheet detail show
  useFetchTimesheetNamesQuery,
  // comments
  useFetchGetallProjetsCommentsQuery,
  useFetchGetallIssueCommentsQuery,
  useFetchGetallTaskCommentsQuery,
  useFetchGetallMilestoneCommentsQuery,
  // clients list
  useFetchClientsShowUsersQuery,
  useFetchClientCompanyNameQuery,
  // budget page
  useFetchBudgetPaymentsQuery,
  useFetchPortalUserQuery,
  useFetchBudgetSummaryTableQuery,
  useFetchBudgetExpenseTableQuery,
  useFetchBudgetAdditionalScopesQuery,
  useFetchBudgetOverviewQuery,
  useFetchTeamUtilisationQuery,
  useAddAdditionalScopeMutation,
  useUpdateAdditionalScopeMutation,
  useDeleteAdditionalScopeMutation,
  // milestone page
  useFetchMilestoneNamesQuery,
  useFetchMilestoneTasksQuery,
  useFetchMilestoneIssuesQuery,
  useFetchMailstoneTaskTimeSheetLogDetailsQuery,
  useFetchMilestoneBasedIsuueFilterQuery,
  useFetchProjectBasedIsuueFilterQuery,
  //  projects page and project based get all
  useFetchGetAllProjectBasedUserNameListQuery,
  useFetchGetAllTaskOrIssueBasedUserNameListQuery,
  useFetchProjectActivityQuery,
  useFetchGetAllProjectBasedTaskNamesQuery,
  useFetchShowAllProjectsQuery,
  useFetchGetAllProjectBasedIssueNamesQuery,
  useFetchViewProjectsByIdQuery,
  // master page and personal settings
  useFetchMasterAllStatusQuery,
  useFetchMasterPriorityStatusQuery,
  useFetchMasterSeverityStatusQuery,
  useFetchPersonalSettingsQuery,
  useFetchMasterRoleQuery,
  useFetchMasterSkillsQuery,
  useFetchMasterDepartmentQuery,
  useFetchGetAllMasterDesignationsQuery,
  // task page
  useFetchTaskListsQuery,
  useFetchTaskNameByIdQuery,
  useFetchTaskSliderQuery,
  useFetchViewTaskByIDQuery,
  useFetchTaskDetailViewQuery,
  useFetchTaskActivityQuery,
  // issue page
  useFetchIssueDetailViewQuery,
  useFetchIssueActivityQuery,
  // milestones
  useFetchMilestoneActivityQuery,
  useFetchMilestoneDetailsQuery,

  // milestone task
  useFetchMilestoneTaskViewQuery,
  useFetchMilestoneShowTasksQuery,
  useFetchMilestoneTaskActivityQuery,
  useFetchGetallMilestoneTaskCommentsQuery,
  // milestone issue
  useFetchMilestoneIsssueViewQuery,
  useFetchMilestoneIssueActivityQuery,
  useFetchGetallMilestoneIssueCommentsQuery,

  // rate per hour
  useFetchRatePerHourQuery,
  // get all users
  useFetchGetallUserListQuery,
  useFetchGetallClientListQuery,
  useFetchAddportalUserQuery,
  useFetchClientDetailsQuery,
  // dashBoard
  useFetchNotificationListQuery,
  useFetchDashboardDetailsQuery,
  useFetchDashboardProjectDetailsQuery,
  useFetchDashboardTaskDetailsQuery,
  useFetchDashboardIsuueDetailsQuery,
  useFetchDashboardTodaysTaskQuery,
  useFetchMyOverduesQuery,
  useFetchDashboardTimesheetDetailsQuery,
  useFetchUserInfoQuery,
  // timesheet detail
  useFetchTimesheetDetailQuery,
  useFetchGetallProjectsQuery,
  useFetchMonthlyReportsQuery,
  // modules
  useFetchTaskModuleQuery,
  useFetchIssueModuleQuery,
  useFetchMilestoneModuleQuery,
  useFetchTimesheetModuleQuery,
  useFetchTotalLogHoursQuery,
  // get all managers
  useFetchGetallManagersQuery,
  useFetchGetManagerByProjectQuery,
  useFetchGetallDepartmentsQuery,
  useFetchGetAllSkillsQuery,
  useFetchDuplicateTaskAllocationQuery,
  // user accessed or based projects , task, issue
  useFetchUserAccessedProjectsQuery,
  useFetchuserbasedIssuesQuery,
  useFetchuserbasedTasksQuery,
  useFetchTaskAllocationQuery,
  // task remainder getusers
  useFetchTaskRemainderUsersQuery,
  // password recommentation
  useFetchPasswordRecommentQuery,
  // TimerActivities
  useFetchTimerActivitiesQuery,
  useFetchTimeLogActivitiesQuery,
  // drafts
  useFetchGetallDraftsQuery,
  // monthly logs
  useFetchMonthlyLogsQuery,
  // projectbased tasklist
  useFetchProjectbasedTasklistQuery,
  // OT LISTS
  useFetchOTListsQuery,
  useFetchOTReportsQuery,
  // issue module dropdown api
  useFetchIssueModuleDropdownQuery,
  // project api
  useFetchProjectListQuery,
  useFetchWorkStatusQuery,
  useFetchAllDepartmentsQuery,
  useFetchBenchSummaryQuery,
  // post api's
  useFetchExportUsersMutation,
  useFetchCreateMasterRolesMutation,
  useFetchCreateMasterDepartmentsMutation,
  useFetchCreateDatasMutation,
  // client testimonial
  useFetchCreateClientTestimonialMutation,
  useFetchGenerateTestimonialLinkMutation,
  useDeleteTestimonialMutation,
  // task remainder
  useFetchCreateRemainderMutation,
  // task reccurence
  useFetchCreatereccurenceMutation,
  // task allocation
  useFetchCreateOrUpdateTaskAllocationMutation,
  // rateperhour create
  useFetchUpdateRatePerHourMutation,
  useFetchDeleteRatePerHourMutation,
  // dark mode
  useUpdateDarkModeMutation,
  // ============================================
  // Appraisal Feedback Hooks
  useFetchAppraisalFeedbacksQuery,
  useFetchAppraisalFeedbackByIdQuery,
  useCreateAppraisalFeedbackMutation,
  useUpdateAppraisalFeedbackMutation,
  useDeleteAppraisalFeedbackMutation,
  useSearchAppraisalFeedbacksMutation,
  useFilterAppraisalFeedbacksMutation,
  // NewAppraisal feedback 
  useGetAllUserWithRoleQuery,

  //========= Workforce Stability ================
  useGetDepartmentUsersListsQuery,
  useGetDepartmentUsersQuery,
  useLazyGetDepartmentUsersQuery,
  useGenerateWorkforceReportMutation,

  // Award Periods
  useFetchAwardPeriodsQuery,

  useCreateAwardMutation,
  useUpdateAwardMutation,
  useDeleteAwardMutation,
  useFetchCertificatesQuery,
  useDownloadCertificateQuery,
  useLazyDownloadCertificateQuery,
  useVerifyCertificateQuery,

  // Resource Earning Module
  useSyncZohoInvoicesMutation,
  useFetchInvoicesQueueQuery,
  useFetchInvoiceSourcesQuery,
  useFetchInvoiceProjectsQuery,
  useParseInvoiceResourcesMutation,
  useAllocateInvoiceMutation,
  useFetchInvoiceByIdQuery,
  useLazyFetchInvoiceByIdQuery,
  useCreateManualInvoiceMutation,
  useDeleteManualInvoiceMutation,
  useFetchInvoiceReferenceIdQuery,
  useLazyFetchInvoiceReferenceIdQuery,
  useFetchProjectResourcesMutation,
  useAllocateContractInvoiceMutation,
  useAllocateFixedCostInvoiceMutation,
  useFetchPreviousInvoicesMutation,
  useFixedCostMapInvoiceMutation,
  useFetchResourceEarningsQuery,
  useLazyFetchResourceEarningsQuery,
  useExportResourceEarningsMutation,
  useFetchAwaitingAllocationQuery,
  useLazyFetchAwaitingAllocationQuery,
  useExportAwaitingAllocationMutation,
  useFetchProjectEarningsQuery,
  useLazyFetchProjectEarningsQuery,

  useUpdateBudgetPaymentMutation,
  useDeleteBudgetPaymentMutation,
} = apiSlice;

// Export the API slice itself (for store integration)
export default apiSlice;
